import { type ChatCounterpart } from '../interface/support-chat';

// Every sentence that names the other party, in one place.
//
// The support desk is addressed as a function ("the support desk") and a peer as a person
// ("Halima"). Keeping the two branches together is what stops a composer saying one thing
// and the quote above it saying another.

/** The first name, for the places where a full name is too long: typing line, quote header. */
export const firstNameOf = (name: string): string => name.trim().split(/\s+/)[0] || name;

export interface ICounterpartCopy {
	/** The composer's accessible name, in full. */
	composerPlaceholder: string;
	/**
	 * The composer's visible placeholder. Shorter than the accessible name because the box sits
	 * between two attach buttons and Send: at 360–390px "Message the support desk" was cut to
	 * "Message the support d…".
	 */
	composerHint: string;
	/** `aria-label` on the transcript. */
	transcriptLabel: string;
	/** The line under the transcript while they compose. */
	typingLine: string;
	/** Who a quote is attributed to when it is not the reader's own. */
	quoteName: string;
	/** Subject of a sentence: "The desk has already read this", "Halima has already read this". */
	subject: string;
	/** Who retracted a message that is not the reader's own. */
	tombstoneActor: string;
}

export const counterpartCopy = (counterpart: ChatCounterpart): ICounterpartCopy => {
	if (counterpart.kind === 'desk') {
		return {
			composerPlaceholder: 'Message the support desk',
			composerHint: 'Message support',
			transcriptLabel: 'Conversation with the support desk',
			typingLine: 'Support desk is typing…',
			quoteName: 'Support desk',
			subject: 'The desk',
			tombstoneActor: 'Support desk',
		};
	}

	const first = firstNameOf(counterpart.name);
	return {
		composerPlaceholder: `Message ${first}`,
		composerHint: `Message ${first}`,
		transcriptLabel: `Conversation with ${counterpart.name}`,
		typingLine: `${first} is typing…`,
		quoteName: first,
		subject: first,
		tombstoneActor: counterpart.name,
	};
};
